import React, { useMemo } from "react";
import { Table, Tag } from "antd";
import { useAppDispatch } from "../../hooks/reduxHook";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { selectDevice, setWifiModalOpen } from "../../reducers/devicesSlice";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import "./index.css";
import AlertCell from "./AlertCell";
import { useAppAlerts } from "../../hooks/useAppAlerts";
import { Device } from "../../types";

interface DeviceTableRecord {
  key: string;
  deviceName: string;
  location: string;
  address: string;
  coordinates: string;
  wifiConnection: string;
  status: "Online" | "Offline";
  hasMaintenanceAlert: boolean;
  hasDetectionAlert: boolean;
}

const TrapTable: React.FC = () => {
  const dispatch = useAppDispatch();

  const { data: alertsData } = useAppAlerts();
  const { data: trapSiteData, isLoading: isTrapSitesLoading } =
    useListTrapSitesQuery();
  const { data: devices, isLoading: isDevicesLoading } = useListDevicesQuery();

  const deviceData = useMemo((): DeviceTableRecord[] => {
    if (!devices?.items || !trapSiteData?.items) return [];
    const alerts = alertsData?.items || [];

    const trapSiteMap = new Map(trapSiteData.items.map((t) => [t._id, t]));

    return devices.items.map((device) => {
      const trapSite = trapSiteMap.get(device.trap_id);
      const deviceAlerts = alerts.filter((a) => a.device === device.uuid);
      const hasMaintenanceAlert = deviceAlerts.some(
        (a) => a.severity === "warning" || a.severity === "danger",
      );
      const hasDetectionAlert = deviceAlerts.some((a) => a.tag === "Detection");

      return {
        key: device.uuid || "",
        deviceName: device.display_name || "N/A",
        location: trapSite?.display_name || "N/A",
        address: trapSite?.address || "N/A",
        coordinates: trapSite?.gps
          ? `${trapSite.gps.lat.toFixed(4)}, ${trapSite.gps.lon.toFixed(4)}`
          : "N/A",
        wifiConnection: device.wifi || "Connect",
        status: device.status === "idle" ? "Online" : "Offline",
        hasMaintenanceAlert,
        hasDetectionAlert,
      };
    });
  }, [devices, trapSiteData, alertsData]);

  const sortIcon = useMemo(
    () =>
      ({ sortOrder }: { sortOrder: "ascend" | "descend" | null }) => {
        if (sortOrder === "ascend")
          return <ChevronUpIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        if (sortOrder === "descend")
          return <ChevronDownIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        return (
          <ChevronDownIcon className="w-5 h-5 opacity-0 group-hover:opacity-100 ml-1" />
        );
      },
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: "Trap ID",
        dataIndex: "deviceName",
        key: "deviceName",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          (a.deviceName || "").localeCompare(b.deviceName || ""),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.location.localeCompare(b.location),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Address",
        dataIndex: "address",
        key: "address",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.address.localeCompare(b.address),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Coordinates",
        dataIndex: "coordinates",
        key: "coordinates",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.coordinates.localeCompare(b.coordinates),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Wifi connection",
        dataIndex: "wifiConnection",
        key: "wifiConnection",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.wifiConnection.localeCompare(b.wifiConnection),
        sortIcon,
        render: (text: string, record: DeviceTableRecord) => (
          <span
            className="text-[#0EA5E9] cursor-pointer"
            onClick={() => {
              dispatch(setWifiModalOpen(true));
              const device = devices?.items.find((d) => d.uuid === record.key);
              if (device) {
                dispatch(selectDevice(device as Device));
              }
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: "Device",
        key: "status",
        dataIndex: "status",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.status.localeCompare(b.status),
        sortIcon,
        render: (status: string) => (
          <Tag
            className={`status-tag ${status === "Online" ? "online" : "offline"}`}
          >
            {status}
          </Tag>
        ),
      },
      {
        title: "Alerts",
        key: "alerts",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) => {
          const getScore = (record: DeviceTableRecord) => {
            let score = 0;
            if (record.hasMaintenanceAlert) score += 10;
            if (record.hasDetectionAlert) score += 5;
            return score;
          };
          return getScore(a) - getScore(b);
        },
        sortIcon,
        render: (_: any, record: DeviceTableRecord) => (
          <AlertCell record={record} />
        ),
      },
    ],
    [devices, dispatch, sortIcon],
  );

  return (
    <div className="bg-white p-6 rounded-lg border border-[#0C4A6E]">
      <Table<DeviceTableRecord>
        columns={columns}
        dataSource={deviceData}
        loading={isTrapSitesLoading || isDevicesLoading}
        pagination={false}
        className="device-table"
      />
    </div>
  );
};

export default TrapTable;
